// Dialogo para vincular un usuario de Wialon/Leasecontrol a uno o mas grupos
// de unidades YA EXISTENTES — analogo al de TRS Rastreo, pero la API de estas
// dos plataformas edita por REEMPLAZO COMPLETO (manda la lista final de
// usuarios/unidades del grupo, no un delta agregar/remover como TRS). Por eso
// hace falta traer primero los miembros ACTUALES del grupo elegido y sumarle
// el usuario nuevo antes de guardar — mismos helpers que ya usa
// GrupoFormDialog para leer ese shape (getGrupoUsuarios/getGrupoUnitIds).
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import {
  getGrupoId,
  getGrupoNombre,
  getGrupoUnitIds,
  getGrupoUsuarios,
  getUsuarioId,
} from "../utils/plataformaGrupoHelpers";
// getGrupoUnitIds sigue usandose para mostrar el conteo de unidades en cada
// fila del checklist (no para el payload de guardado).

// `cuentas` (opcional): [{ id, nombre }] de la cuenta del usuario. Si viene, solo se listan los grupos de esas cuentas (un usuario solo puede
// ver grupos de su propia cuenta); si viene vacío no se lista nada para no ofrecer grupos de otros clientes.
export default function AsignarGrupoDialog({ open, usuarioId, usuarioNombre, listGroups, updateGroup, onClose, onSaved, cuentas }) {
  const filtraPorCuenta = Array.isArray(cuentas);
  const cuentasKey = filtraPorCuenta ? cuentas.map((c) => c.id).join(",") : "";
  const [grupos, setGrupos] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [loading, setLoading] = useState(false);
  // "filter" es lo que se ve en el input (cambia en cada tecla); "filtroActivo"
  // es lo que realmente se manda a listGroups, con debounce — sin esto, cada
  // tecla dispara un fetch nuevo y una respuesta vieja (de cuando habia menos
  // letras escritas) puede llegar DESPUES de la mas reciente y pisar la lista
  // ya filtrada con el listado completo otra vez.
  const [filter, setFilter] = useState("");
  const [filtroActivo, setFiltroActivo] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    setFilter("");
    setFiltroActivo("");
    setPage(1);
    setSelectedIds([]);
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setFiltroActivo(filter.trim());
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [filter, open]);

  const cargar = useCallback(async () => {
    if (!open) return;
    if (filtraPorCuenta && !cuentasKey) { setGrupos([]); setTotal(0); return; }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const resp = await listGroups({ search: filtroActivo, page, limit: pageSize, ...(filtraPorCuenta ? { cuentas: cuentasKey } : {}) });
      if (requestId !== requestIdRef.current) return; // respuesta obsoleta, se ignora
      const payload = resp?.data || {};
      setGrupos(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total || 0));
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      setGrupos([]);
      setTotal(0);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [open, listGroups, filtroActivo, page, pageSize, filtraPorCuenta, cuentasKey]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleFilterChange = (value) => {
    setFilter(value);
    setPage(1);
  };

  const yaVinculadoIds = new Set(
    grupos
      .filter((g) => getGrupoUsuarios(g).map(getUsuarioId).map(String).includes(String(usuarioId)))
      .map((g) => String(getGrupoId(g)))
  );

  const selectedSet = new Set(selectedIds.map(String));
  const toggle = (id) => {
    const value = String(id);
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    setSelectedIds([...next]);
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!selectedIds.length) {
      const msg = "Selecciona al menos un grupo.";
      setError(msg);
      setSnack({ open: true, msg });
      return;
    }
    setSaving(true);
    setError("");
    try {
      const seleccionados = grupos.filter((g) => selectedSet.has(String(getGrupoId(g))));
      await Promise.all(
        seleccionados.map((g) => {
          // Solo se toca la membresia de USUARIOS — mandar tambien "units" (aunque
          // sea la misma lista sin cambios) dispara una llamada extra a
          // unit_group/update_units en Wialon que no hace falta para esto y que
          // en la practica devolvio error 6 (rechazada).
          const userIds = [...new Set([...getGrupoUsuarios(g).map(getUsuarioId).map(String), String(usuarioId)])];
          return updateGroup(getGrupoId(g), { userIds });
        })
      );
      // Se espera a que el padre termine de recargar el arbol antes de cerrar
      // (setSaving sigue en true, spinner visible) — si no, el dialogo se cierra
      // antes de que el cambio se refleje y el usuario ve el arbol viejo un rato.
      await onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo asignar el/los grupo(s).";
      setError(msg);
      setSnack({ open: true, msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem", fontWeight: 900 }}>
          Asignar grupo a {usuarioNombre || "usuario"}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5, display: "flex", flexDirection: "column", gap: 1, overflowY: "visible" }}>
          {!!error && <Alert severity="error">{error}</Alert>}
          {filtraPorCuenta && (
            <Alert severity={cuentasKey ? "info" : "warning"} sx={{ py: 0, fontSize: "0.75rem" }}>
              {cuentasKey
                ? `Solo se muestran los grupos de la cuenta: ${cuentas.map((c) => c.nombre || `ID ${c.id}`).join(", ")}.`
                : "No se pudo determinar la cuenta de este usuario, así que no se ofrecen grupos. Recarga la lista e inténtalo de nuevo."}
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            placeholder="Buscar grupo..."
            value={filter}
            onChange={(e) => handleFilterChange(e.target.value)}
            disabled={saving}
            InputProps={{ endAdornment: loading ? <CircularProgress color="inherit" size={16} /> : null }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>Grupos</Typography>
            <Chip size="small" label={`${selectedSet.size} seleccionado(s)`} sx={{ height: 20, fontSize: "0.68rem" }} />
          </Box>
          <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, height: 320, overflowY: "auto" }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : grupos.length === 0 ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", p: 1.5, fontStyle: "italic" }}>
                Sin grupos para mostrar.
              </Typography>
            ) : (
              grupos.map((g) => {
                const id = getGrupoId(g);
                const seleccionable = !yaVinculadoIds.has(String(id));
                const checked = selectedSet.has(String(id));
                const cantidadUnidades = getGrupoUnitIds(g).length;
                return (
                  <Box
                    key={id}
                    onClick={() => seleccionable && toggle(id)}
                    sx={{
                      display: "flex", alignItems: "center", gap: 0.5, px: 0.75, py: 0.4,
                      cursor: seleccionable ? "pointer" : "not-allowed",
                      opacity: seleccionable ? 1 : 0.45,
                      "&:hover": seleccionable ? { bgcolor: "action.hover" } : undefined,
                    }}
                  >
                    <Checkbox size="small" checked={checked} disabled={!seleccionable || saving} sx={{ p: 0.25 }} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: checked ? 900 : 600 }}>
                        {getGrupoNombre(g)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {seleccionable ? `${cantidadUnidades} unidad(es)` : "Ya pertenece a este grupo"}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
          <TablePagination
            component="div"
            count={total}
            page={page - 1}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[15, 30, 50]}
            labelRowsPerPage="Limite"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            sx={{ "& .MuiToolbar-root": { pl: 0, pr: 0, minHeight: 40 } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button size="small" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Asignar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
